import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FreshnessBadge,
  PageHeader,
  Skeleton,
  Table,
} from "@evolua/ui";
import { useState } from "react";
import { QueryErrorState } from "../api/QueryErrorState.js";
import { useDownloadMePayslip, useMePayslip, useMePayslips } from "../api/use-me.js";

function formatCurrency(amount: number): string {
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatCompetence(competence: string): string {
  const [year, month] = competence.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

function PayslipDetail({ id }: { id: string }) {
  const query = useMePayslip(id);
  const download = useDownloadMePayslip();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lastAudit, setLastAudit] = useState<string | null>(null);

  async function onDownload() {
    setDownloadError(null);
    try {
      const result = await download.mutateAsync(id);
      const blob = new Blob([result.content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      setLastAudit(result.auditId);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Falha ao baixar holerite");
    }
  }

  if (query.isPending) return <Skeleton height="6rem" label="Carregando holerite" />;
  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const detail = query.data;
  const lines = detail.lines.map((line, idx) => ({
    ...line,
    rowId: `${line.label}-${idx}`,
  }));

  return (
    <div className="evolua-stack evolua-stack--3">
      <Table
        caption="Linhas"
        getRowId={(line) => line.rowId}
        columns={[
          {
            id: "type",
            header: "Tipo",
            cell: (line) => (line.type === "EARNING" ? "+" : "-"),
          },
          { id: "label", header: "Descrição", cell: (line) => line.label },
          {
            id: "amount",
            header: "Valor",
            align: "numeric",
            cell: (line) => formatCurrency(line.amount),
          },
        ]}
        rows={lines}
      />
      <p>
        <strong>Descontos:</strong> {formatCurrency(detail.discounts)} ·{" "}
        <strong>Líquido:</strong> {formatCurrency(detail.netAmount)}
      </p>
      <div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={download.isPending}
          onClick={() => void onDownload()}
        >
          {download.isPending ? "Baixando…" : "Baixar holerite"}
        </Button>
      </div>
      {downloadError ? <Alert tone="danger">{downloadError}</Alert> : null}
      {lastAudit ? <p className="evolua-muted">Auditado · {lastAudit}</p> : null}
    </div>
  );
}

export function HoleritePage() {
  const query = useMePayslips();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Holerite"
        lead="Contracheques do próprio colaborador. Cada visualização e download é auditado (payroll.view — FASE-06-T34)."
      />

      {query.isPending ? (
        <div className="evolua-stack evolua-stack--2" aria-busy="true">
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
        </div>
      ) : query.isError ? (
        <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.data.length === 0 ? (
        <EmptyState
          title="Sem holerites"
          description="Nenhum holerite disponível ainda."
        />
      ) : (
        <>
          <Table
            caption="Holerites"
            getRowId={(payslip) => payslip.id}
            columns={[
              {
                id: "competence",
                header: "Competência",
                cell: (payslip) => formatCompetence(payslip.competence),
              },
              {
                id: "net",
                header: "Líquido",
                align: "numeric",
                cell: (payslip) => formatCurrency(payslip.netAmount),
              },
              {
                id: "sync",
                header: "Sincronização",
                cell: (payslip) => <FreshnessBadge syncedAt={payslip.syncedAt} />,
              },
              {
                id: "actions",
                header: "Ações",
                cell: (payslip) => (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setExpanded((prev) => (prev === payslip.id ? null : payslip.id))
                    }
                  >
                    {expanded === payslip.id ? "Ocultar" : "Ver detalhe"}
                  </Button>
                ),
              },
            ]}
            rows={query.data.data}
          />
          {expanded ? (
            <Card title="Detalhe" className="evolua-mt">
              <PayslipDetail id={expanded} />
            </Card>
          ) : null}
        </>
      )}
      <p className="evolua-mt">
        <Badge tone="neutral">Mock-first</Badge> Documento simulado até o piloto TOTVS RM
        (P-001) gerar o PDF assinado real.
      </p>
    </>
  );
}
